import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Preview as PreviewIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';

const steps = ['Upload CSV', 'Map Fields', 'Preview Data', 'Import'];

const expectedFields = [
  { key: 'name', label: 'Name', required: false, description: 'Recipient name' },
  { key: 'email', label: 'Email', required: false, description: 'Contact email' },
  { key: 'walletAddress', label: 'Wallet Address', required: true, description: 'Ethereum/Avalanche wallet address' },
  { key: 'allocation', label: 'Allocation', required: false, description: 'Token allocation amount' },
  { key: 'unlockSchedule', label: 'Unlock Schedule', required: false, description: 'Vesting schedule details' },
  { key: 'scheduleStart', label: 'Schedule Start', required: false, description: 'When vesting begins' },
  { key: 'kycStatus', label: 'KYC Status', required: false, description: 'KYC verification status' },
  { key: 'vestingType', label: 'Vesting Type', required: false, description: 'e.g., Investor, Team, Advisor' },
  { key: 'pricePerToken', label: 'Price Per Token', required: false, description: 'Token price' },
  { key: 'totalPrice', label: 'Total Price', required: false, description: 'Total allocation value' }
];

function CSVImportManager() {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [batchName, setBatchName] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const queryClient = useQueryClient();

  // Fetch import history
  const { data: importHistory, isLoading: historyLoading, refetch: refetchHistory } = useQuery({
    queryKey: ['csvImportHistory'],
    queryFn: async () => {
      const response = await api.get('/allocations/import/history');
      return response.data;
    }
  });

  // CSV validation mutation
  const validateMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await api.post('/allocations/import/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.isValid) {
        showAlert('CSV validation successful!', 'success');
        setActiveStep(1);
      } else {
        showAlert(`CSV validation failed: ${data.missingFields.join(', ')} fields missing`, 'error');
      }
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Validation failed', 'error');
    }
  });

  // CSV import mutation
  const importMutation = useMutation({
    mutationFn: async ({ file, batchName, fieldMapping }) => {
      const formData = new FormData();
      formData.append('csvFile', file);
      formData.append('batchName', batchName);
      formData.append('fieldMapping', JSON.stringify(fieldMapping));
      
      const response = await api.post('/allocations/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    onSuccess: (data) => {
      showAlert(`Import completed! ${data.stats.successfulRows} rows imported successfully.`, 'success');
      setImportDialogOpen(false);
      resetForm();
      refetchHistory();
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Import failed', 'error');
    }
  });

  const showAlert = (message, severity = 'info') => {
    const alert = { id: Date.now(), message, severity };
    setAlerts(prev => [...prev, alert]);
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    }, 5000);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const csvFiles = files.filter(file => file.type === 'text/csv');
    
    if (csvFiles.length === 0) {
      showAlert('Please select at least one valid CSV file', 'error');
      return;
    }
    
    if (csvFiles.length !== files.length) {
      showAlert(`${files.length - csvFiles.length} non-CSV files were ignored`, 'warning');
    }
    
    setSelectedFiles(csvFiles);
    
    if (csvFiles.length === 1) {
      // Single file - existing flow
      setSelectedFile(csvFiles[0]);
      parseCSVFile(csvFiles[0]);
    } else {
      // Multiple files - batch processing
      processBatchFiles(csvFiles);
    }
  };

  const autoMapFields = (headers) => {
    const mapping = {};
    
    // Auto-map based on common header patterns
    headers.forEach(header => {
      const lowerHeader = header.toLowerCase().trim();
      
      if (lowerHeader.includes('name') && !lowerHeader.includes('batch')) {
        mapping.name = header;
      } else if (lowerHeader.includes('email')) {
        mapping.email = header;
      } else if (lowerHeader.includes('wallet') || lowerHeader.includes('address')) {
        mapping.walletAddress = header;
      } else if (lowerHeader.includes('allocation') && lowerHeader.includes('amount')) {
        mapping.allocation = header;
      } else if (lowerHeader.includes('unlock') && lowerHeader.includes('schedule') && !lowerHeader.includes('start')) {
        mapping.vestingType = header;
      } else if (lowerHeader.includes('vesting') && lowerHeader.includes('type')) {
        mapping.vestingType = header;
      } else if (lowerHeader.includes('kyc')) {
        mapping.kycStatus = header;
      } else if (lowerHeader.includes('price') && lowerHeader.includes('token')) {
        mapping.pricePerToken = header;
      } else if (lowerHeader.includes('total') && lowerHeader.includes('price')) {
        mapping.totalPrice = header;
      } else if (lowerHeader.includes('schedule') && lowerHeader.includes('start')) {
        mapping.scheduleStart = header;
      } else if (lowerHeader.includes('unlock') || lowerHeader.includes('schedule')) {
        mapping.unlockSchedule = header;
      }
    });
    
    setFieldMapping(mapping);
    return mapping;
  };

  const generatePreviewWithMapping = (mapping) => {
    const preview = csvData.slice(0, 10).map(row => {
      const mappedRow = {};
      Object.entries(mapping).forEach(([fieldKey, csvHeader]) => {
        mappedRow[fieldKey] = row[csvHeader] || '';
      });
      return mappedRow;
    });
    setPreviewData(preview);
  };

  const processBatchFiles = async (files) => {
    showAlert(`Processing ${files.length} CSV files...`, 'info');
    
    let totalRows = 0;
    let allData = [];
    let commonHeaders = null;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fileData = await parseCSVFileAsync(file);
        
        // Check if headers are consistent across files
        if (commonHeaders === null) {
          commonHeaders = fileData.headers;
        } else if (JSON.stringify(commonHeaders) !== JSON.stringify(fileData.headers)) {
          showAlert(`Warning: File "${file.name}" has different headers. Skipping.`, 'warning');
          continue;
        }
        
        allData = allData.concat(fileData.rows);
        totalRows += fileData.rows.length;
        
      } catch (error) {
        showAlert(`Error processing file "${file.name}": ${error.message}`, 'error');
      }
    }
    
    if (allData.length === 0) {
      showAlert('No valid data found in any files', 'error');
      return;
    }
    
    // Set combined data
    setCsvData(allData);
    setCsvHeaders(commonHeaders);
    
    // Generate batch name from multiple files
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    setBatchName(`Batch_${files.length}files_${timestamp}`);
    
    // Auto-map and preview
    const fieldMapping = autoMapFields(commonHeaders);
    setTimeout(() => {
      generatePreviewWithMapping(fieldMapping);
      setActiveStep(2);
    }, 100);
    
    showAlert(`Successfully processed ${files.length} files with ${totalRows} total rows`, 'success');
  };

  const parseCSVFileAsync = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          if (!text.trim()) {
            reject(new Error('File is empty'));
            return;
          }
          
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            reject(new Error('File must have at least a header and one data row'));
            return;
          }
          
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          const rows = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            return row;
          });
          
          resolve({ headers, rows });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const parseCSVFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      
      if (!text.trim()) {
        showAlert('CSV file is empty', 'error');
        return;
      }

      try {
        // Proper CSV parsing that handles quoted fields with commas and newlines
        const parseCSV = (csvText) => {
          const result = [];
          let current = '';
          let inQuotes = false;
          let row = [];
          
          for (let i = 0; i < csvText.length; i++) {
            const char = csvText[i];
            const nextChar = csvText[i + 1];
            
            if (char === '"') {
              if (inQuotes && nextChar === '"') {
                // Handle escaped quotes
                current += '"';
                i++; // Skip next quote
              } else {
                // Toggle quote state
                inQuotes = !inQuotes;
              }
            } else if (char === ',' && !inQuotes) {
              // End of field
              row.push(current.trim());
              current = '';
            } else if ((char === '\n' || char === '\r') && !inQuotes) {
              // End of row
              if (current.trim() || row.length > 0) {
                row.push(current.trim());
                if (row.some(cell => cell.length > 0)) {
                  result.push(row);
                }
                row = [];
                current = '';
              }
            } else {
              current += char;
            }
          }
          
          // Handle last field/row
          if (current.trim() || row.length > 0) {
            row.push(current.trim());
            if (row.some(cell => cell.length > 0)) {
              result.push(row);
            }
          }
          
          return result;
        };

        const parsedData = parseCSV(text);
        
        if (parsedData.length === 0) {
          showAlert('No valid data found in CSV file', 'error');
          return;
        }

        // Clean headers - remove newlines and extra spaces
        const headers = parsedData[0].map(h => 
          h.replace(/\n/g, ' ')
           .replace(/\r/g, ' ')
           .replace(/\s+/g, ' ')
           .trim()
        );
        
        // Parse data rows
        const data = parsedData.slice(1).map(row => {
          const rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = (row[index] || '').trim();
          });
          return rowData;
        });

        console.log('Parsed headers:', headers);
        console.log('Sample data:', data.slice(0, 3));

        setCsvHeaders(headers);
        setCsvData(data);
        
        // Auto-map fields based on header names (improved matching)
        const autoMapping = {};
        expectedFields.forEach(field => {
          const matchingHeader = headers.find(header => {
            const headerLower = header.toLowerCase();
            const fieldKeyLower = field.key.toLowerCase();
            const fieldLabelLower = field.label.toLowerCase();
            
            // Direct matches
            if (headerLower.includes(fieldKeyLower) || 
                headerLower.includes(fieldLabelLower) ||
                fieldLabelLower.includes(headerLower)) {
              return true;
            }
            
            // Special case mappings
            if (fieldKeyLower === 'walletaddress' && 
                (headerLower.includes('wallet') || headerLower.includes('address'))) {
              return true;
            }
            if (fieldKeyLower === 'allocation' && 
                (headerLower.includes('allocation') || headerLower.includes('amount'))) {
              return true;
            }
            if (fieldKeyLower === 'vestingtype' && 
                (headerLower.includes('category') || headerLower.includes('type'))) {
              return true;
            }
            
            return false;
          });
          
          if (matchingHeader) {
            autoMapping[field.key] = matchingHeader;
          }
        });
        
        console.log('Auto-mapped fields:', autoMapping);
        setFieldMapping(autoMapping);
        
        // Set default batch name from filename
        const filename = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
        const cleanFilename = filename.replace(/[^a-zA-Z0-9\s-_]/g, ' ').trim(); // Clean filename
        setBatchName(cleanFilename);
        
        // Auto-map fields based on header names and generate preview
        const fieldMapping = autoMapFields(headers);
        
        // Generate preview with the auto-mapping
        setTimeout(() => {
          generatePreviewWithMapping(fieldMapping);
          // Jump directly to preview step (skip field mapping)
          setActiveStep(2);
        }, 100);
      } catch (error) {
        console.error('CSV parsing error:', error);
        showAlert('Error parsing CSV file. Please check the file format.', 'error');
      }
    };
    reader.readAsText(file);
  };

  const validateFieldMapping = () => {
    const errors = [];
    const requiredFields = expectedFields.filter(f => f.required);
    
    requiredFields.forEach(field => {
      if (!fieldMapping[field.key]) {
        errors.push(`Required field "${field.label}" is not mapped`);
      }
    });

    // Validate wallet addresses in preview
    if (fieldMapping.walletAddress) {
      const walletErrors = [];
      csvData.slice(0, 10).forEach((row, index) => {
        const address = row[fieldMapping.walletAddress];
        if (address && !/^0x[a-fA-F0-9]{40}$/.test(address)) {
          walletErrors.push(`Row ${index + 2}: Invalid wallet address "${address}"`);
        }
      });
      errors.push(...walletErrors);
    }

    setValidationErrors(errors);
    
    if (errors.length === 0) {
      generatePreview();
      setActiveStep(2);
    } else {
      showAlert(`Validation failed: ${errors.length} errors found`, 'error');
    }
  };

  const generatePreview = () => {
    const preview = csvData.slice(0, 10).map(row => {
      const mappedRow = {};
      Object.entries(fieldMapping).forEach(([fieldKey, csvHeader]) => {
        mappedRow[fieldKey] = row[csvHeader] || '';
      });
      return mappedRow;
    });
    setPreviewData(preview);
  };

  const handleImport = () => {
    if (!batchName.trim()) {
      showAlert('Please enter a batch name', 'error');
      return;
    }

    // Import directly without confirmation dialog
    importMutation.mutate({
      file: selectedFile,
      batchName: batchName.trim(),
      fieldMapping
    });
  };


  const resetForm = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setSelectedFiles([]);
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMapping({});
    setBatchName('');
    setValidationErrors([]);
    setPreviewData([]);
  };

  const getStepIcon = (step) => {
    if (step < activeStep) return <CheckIcon />;
    if (step === activeStep) return step + 1;
    return step + 1;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Alerts */}
      {alerts.map((alert) => (
        <Alert 
          key={alert.id} 
          severity={alert.severity} 
          sx={{ mb: 2 }}
          onClose={() => setAlerts(prev => prev.filter(a => a.id !== alert.id))}
        >
          {alert.message}
        </Alert>
      ))}

      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
        CSV Import Manager
      </Typography>

      <Grid container spacing={3}>
        {/* Import Wizard */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Stepper activeStep={activeStep} orientation="vertical">
                {/* Step 1: Upload CSV */}
                <Step>
                  <StepLabel icon={getStepIcon(0)}>Upload CSV File</StepLabel>
                  <StepContent>
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadIcon />}
                        sx={{ mb: 2, mr: 2 }}
                      >
                        Select CSV Files
                        <input
                          type="file"
                          accept=".csv"
                          multiple
                          hidden
                          onChange={handleFileSelect}
                        />
                      </Button>
                      
                      {selectedFiles.length > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          {selectedFiles.length === 1 
                            ? `Selected: ${selectedFiles[0].name} (${(selectedFiles[0].size / 1024).toFixed(1)} KB)`
                            : `Selected ${selectedFiles.length} files (${(selectedFiles.reduce((sum, f) => sum + f.size, 0) / 1024).toFixed(1)} KB total)`
                          }
                        </Alert>
                      )}

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Expected CSV format: Name, Email, Wallet, Allocation, KYC, Vesting, etc.
                      </Typography>

                      {csvHeaders.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Detected Headers ({csvData.length} rows):
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {csvHeaders.map((header, index) => (
                              <Chip key={index} label={header} size="small" />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </StepContent>
                </Step>

                {/* Step 2: Map Fields */}
                <Step>
                  <StepLabel icon={getStepIcon(1)}>Map CSV Fields</StepLabel>
                  <StepContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Map your CSV columns to the expected fields:
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {expectedFields.map((field) => (
                        <Grid item xs={12} sm={6} key={field.key}>
                          <FormControl fullWidth size="small">
                            <InputLabel>
                              {field.label} {field.required && '*'}
                            </InputLabel>
                            <Select
                              value={fieldMapping[field.key] || ''}
                              onChange={(e) => setFieldMapping(prev => ({
                                ...prev,
                                [field.key]: e.target.value
                              }))}
                              label={`${field.label} ${field.required ? '*' : ''}`}
                            >
                              <MenuItem value="">
                                <em>Not mapped</em>
                              </MenuItem>
                              {csvHeaders.map((header) => (
                                <MenuItem key={header} value={header}>
                                  {header}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary">
                            {field.description}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>

                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={validateFieldMapping}
                        startIcon={<PreviewIcon />}
                        sx={{ mr: 1 }}
                      >
                        Validate & Preview
                      </Button>
                      <Button onClick={() => setActiveStep(0)}>
                        Back
                      </Button>
                    </Box>

                    {validationErrors.length > 0 && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">Validation Errors:</Typography>
                        <List dense>
                          {validationErrors.map((error, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <ErrorIcon color="error" />
                              </ListItemIcon>
                              <ListItemText primary={error} />
                            </ListItem>
                          ))}
                        </List>
                      </Alert>
                    )}
                  </StepContent>
                </Step>

                {/* Step 3: Preview Data */}
                <Step>
                  <StepLabel icon={getStepIcon(2)}>Preview Data</StepLabel>
                  <StepContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Preview of first 10 rows with mapped fields:
                      {selectedFiles.length > 1 && (
                        <Chip 
                          label={`${selectedFiles.length} files combined`} 
                          size="small" 
                          color="primary" 
                          sx={{ ml: 1 }} 
                        />
                      )}
                    </Typography>

                    <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Row</TableCell>
                            {expectedFields.map((field) => (
                              fieldMapping[field.key] && (
                                <TableCell key={field.key}>
                                  {field.label}
                                  {field.required && <span style={{ color: 'red' }}>*</span>}
                                </TableCell>
                              )
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewData.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell>{index + 2}</TableCell>
                              {expectedFields.map((field) => (
                                fieldMapping[field.key] && (
                                  <TableCell key={field.key}>
                                    {field.key === 'walletAddress' ? (
                                      <Tooltip title={row[field.key]}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                          {row[field.key] ? `${row[field.key].slice(0, 6)}...${row[field.key].slice(-4)}` : ''}
                                        </Typography>
                                      </Tooltip>
                                    ) : (
                                      row[field.key] || '-'
                                    )}
                                  </TableCell>
                                )
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      Total rows to import: {csvData.length}
                    </Alert>

                    <Box>
                      <Button
                        variant="contained"
                        onClick={() => setActiveStep(3)}
                        startIcon={<CheckIcon />}
                        sx={{ mr: 1 }}
                      >
                        Proceed to Import
                      </Button>
                      <Button onClick={() => setActiveStep(1)}>
                        Back to Mapping
                      </Button>
                    </Box>
                  </StepContent>
                </Step>

                {/* Step 4: Import */}
                <Step>
                  <StepLabel icon={getStepIcon(3)}>Import Data</StepLabel>
                  <StepContent>
                    <TextField
                      fullWidth
                      label="Batch Name"
                      value={batchName}
                      onChange={(e) => setBatchName(e.target.value)}
                      placeholder="e.g., Seed Round 2024"
                      sx={{ mb: 2 }}
                      required
                    />

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Ready to import {csvData.length} wallet records. This action cannot be undone.
                    </Typography>

                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleImport}
                        startIcon={<UploadIcon />}
                        disabled={!batchName.trim()}
                        sx={{ mr: 1 }}
                      >
                        Import Now
                      </Button>
                      <Button onClick={() => setActiveStep(2)}>
                        Back to Preview
                      </Button>
                      <Button onClick={resetForm} sx={{ ml: 1 }}>
                        Start Over
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        {/* Import History */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Import History</Typography>
                <IconButton onClick={refetchHistory} size="small">
                  <RefreshIcon />
                </IconButton>
              </Box>

              {historyLoading ? (
                <LinearProgress />
              ) : (
                <List>
                  {importHistory?.batches?.slice(0, 10).map((batch) => (
                    <React.Fragment key={batch.id}>
                      <ListItem>
                        <ListItemIcon>
                          {batch.status === 'COMPLETED' ? (
                            <SuccessIcon color="success" />
                          ) : batch.status === 'FAILED' ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <WarningIcon color="warning" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={batch.batchName}
                          secondary={
                            <Box>
                              <Typography variant="caption">
                                {new Date(batch.createdAt).toLocaleDateString()}
                              </Typography>
                              <br />
                              <Typography variant="caption">
                                {batch.successfulRows || 0} success, {batch.failedRows || 0} failed
                              </Typography>
                            </Box>
                          }
                        />
                        <Chip 
                          label={batch.status} 
                          size="small"
                          color={batch.status === 'COMPLETED' ? 'success' : batch.status === 'FAILED' ? 'error' : 'warning'}
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Field Reference */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Field Reference</Typography>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Expected CSV Fields</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {expectedFields.map((field) => (
                      <ListItem key={field.key}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {field.label}
                              {field.required && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                            </Box>
                          }
                          secondary={field.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Import Confirmation Dialog */}
    </Box>
  );
}

export default CSVImportManager;
